import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './style.module.css';
import Typography from '../../../../components/Typography/Typography';
import FlatButtons from '../../../../components/FlatButton/FlatButton';
import { FilterIcon, NewsLetterChevron } from '../../../../assets/svgs';
import { useGetSellerDashboardRecentOrdersQuery } from '../../../../redux/services/dashboardApi';
import { useGetStoreByOwnerQuery } from '../../../../redux/services/storesApi';
import type { RootState } from '../../../../redux/store';

interface Order {
  id: string;
  store: string;
  customer: string;
  total: string;
  items: number;
  status: 'Completed' | 'Pending' | 'Processing';
}

export const DashboardStoreData: React.FC = () => {
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('week');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);

  const { data: recentOrdersData, isLoading: ordersLoading, isError: ordersError } = useGetSellerDashboardRecentOrdersQuery({
    period,
    limit: 10,
    ...(selectedStoreId !== 'all' && { store_id: selectedStoreId }),
  });

  const { data: storesData, isLoading: storesLoading, isError: storesError } = useGetStoreByOwnerQuery();

  const recentOrders: Order[] = useMemo(() => {
    if (!recentOrdersData) return [];

    return recentOrdersData.map((order) => ({
      id: `#${order._id.slice(-4)}`,
      store: order.store_name,
      customer: order.customer_name,
      total: `$${order.total_amount.toFixed(2)}`,
      items: order.total_items,
      status: order.status.charAt(0).toUpperCase() + order.status.slice(1) as 'Completed' | 'Pending' | 'Processing',
    }));
  }, [recentOrdersData]);

  const stores = useMemo(() => {
    if (!storesData) return [];

    return storesData.map((store) => ({
      id: store._id,
      name: store.name,
      category: store.category,
      location: store.location,
      logo_url: store.logo_url,
      slug: store.slug,
      store_link: store.store_link,
    }));
  }, [storesData]);

  const handleStoreClick = (storeId: string): void => {
    console.log('Navigate to store:', storeId);
  };

  const handlePeriodChange = (newPeriod: 'week' | 'month' | 'year'): void => {
    setPeriod(newPeriod);
    setShowFilterMenu(false);
  };

  const getStoreIcon = (category: string): string => {
    const categoryIcons: Record<string, string> = {
      sports: '/dashBoardIcons/dashBoardPage/completedIcon.svg',
      electronics: '/dashBoardIcons/dashBoardPage/pendingIcon.svg',
      fashion: '/dashBoardIcons/dashBoardPage/cancelledIcon.svg',
    };
    return categoryIcons[category.toLowerCase()] || '/dashBoardIcons/dashBoardPage/completedIcon.svg';
  };

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.mainContentGrid}>
        <div className={styles.ordersCard}>
          <div className={styles.cardHeader}>
            <Typography
              variant="heading3"
              fontFamily="satoshi"
              weight="bold"
              className={styles.cardTitle}
            >
              Recent Orders
            </Typography>
            <div style={{ position: 'relative' }}>
              <FlatButtons
                className={styles.filterButton}
                onClick={() => setShowFilterMenu(!showFilterMenu)}
              >
                <img
                  src={FilterIcon}
                  alt="Filter"
                  className={styles.filterIcon}
                />
              </FlatButtons>

              {showFilterMenu && (
                <div className={styles.filterMenu}>
                  <div
                    className={`${styles.filterOption} ${period === 'week' ? styles.filterOptionActive : ''}`}
                    onClick={() => handlePeriodChange('week')}
                  >
                    <Typography variant="bodyRegular" fontFamily="satoshi" weight="regular">
                      This Week
                    </Typography>
                  </div>
                  <div
                    className={`${styles.filterOption} ${period === 'month' ? styles.filterOptionActive : ''}`}
                    onClick={() => handlePeriodChange('month')}
                  >
                    <Typography variant="bodyRegular" fontFamily="satoshi" weight="regular">
                      This Month
                    </Typography>
                  </div>
                  <div
                    className={`${styles.filterOption} ${period === 'year' ? styles.filterOptionActive : ''}`}
                    onClick={() => handlePeriodChange('year')}
                  >
                    <Typography variant="bodyRegular" fontFamily="satoshi" weight="regular">
                      This Year
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <Typography
                variant="heading3"
                fontFamily="satoshi"
                weight="bold"
                className={styles.headerCell}>
                ORDER ID
              </Typography>
              <Typography
                variant="heading3"
                fontFamily="satoshi"
                weight="bold"
                className={styles.headerCell}>
                STORE
              </Typography>
              <Typography
                variant="heading3"
                fontFamily="satoshi"
                weight="bold"
                className={styles.headerCell}>
                CUSTOMER
              </Typography>
              <Typography
                variant="heading3"
                fontFamily="satoshi"
                weight="bold"
                className={styles.headerCell}>
                TOTAL
              </Typography>
              <Typography
                variant="heading3"
                fontFamily="satoshi"
                weight="bold"
                className={styles.headerCell}>
                ITEMS
              </Typography>
              <Typography
                variant="heading3"
                fontFamily="satoshi"
                weight="bold"
                className={styles.headerCell}>
                STATUS
              </Typography>
            </div>

            <div className={styles.tableBody}>
              {ordersLoading ? (
                <div className={styles.tableRow}>
                  <div style={{ gridColumn: '1 / -1', justifyContent: 'center' }}>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="regular"
                      className={styles.orderIdCell}>
                      Loading orders...
                    </Typography>
                  </div>
                </div>
              ) : ordersError ? (
                <div className={styles.tableRow}>
                  <div style={{ gridColumn: '1 / -1', justifyContent: 'center', color: '#EF4444' }}>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="regular"
                      className={styles.orderIdCell}>
                      Error loading orders. Please try again.
                    </Typography>
                  </div>
                </div>
              ) : recentOrders.length === 0 ? (
                <div className={styles.tableRow}>
                  <div style={{ gridColumn: '1 / -1', justifyContent: 'center' }}>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="regular"
                      className={styles.orderIdCell}>
                      No recent orders for this {period}
                    </Typography>
                  </div>
                </div>
              ) : (
                recentOrders.map((order, index) => (
                  <div key={`${order.id}-${index}`} className={styles.tableRow}>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="bold"
                      className={styles.orderIdCell}>
                      {order.id}
                    </Typography>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="regular"
                      className={styles.storeCell}>
                      {order.store}
                    </Typography>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="bold"
                      className={styles.customerCell}>
                      {order.customer}
                    </Typography>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="bold"
                      className={styles.totalCell}>
                      {order.total}
                    </Typography>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="bold"
                      className={styles.itemsCell}>
                      {order.items}
                    </Typography>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="regular"
                      className={styles.statusCell}>
                      {order.status}
                    </Typography>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className={styles.storesCard}>
          <Typography
            variant="heading3"
            fontFamily="satoshi"
            weight="bold"
            className={styles.cardTitle}
          >
            Your Stores
          </Typography>

          <div className={styles.storesList}>
            {storesLoading ? (
              <div className={styles.storeItem}>
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <Typography
                    variant="heading3"
                    fontFamily="satoshi"
                    weight="regular">
                    Loading stores...
                  </Typography>
                </div>
              </div>
            ) : storesError ? (
              <div className={styles.storeItem}>
                <div style={{ width: '100%', textAlign: 'center', color: '#EF4444' }}>
                  <Typography
                    variant="heading3"
                    fontFamily="satoshi"
                    weight="regular"
                  >
                    Error loading stores
                  </Typography>
                </div>
              </div>
            ) : stores.length === 0 ? (
              <div className={styles.storeItem}>
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <Typography
                    variant="heading3"
                    fontFamily="satoshi"
                    weight="regular"
                  >
                    No stores found
                  </Typography>
                </div>
              </div>
            ) : (
              stores.map((store) => (
                <div
                  key={store.id}
                  className={styles.storeItem}
                  onClick={() => handleStoreClick(store.id)}
                >
                  <div className={styles.storeIcon}>
                    {store.logo_url ? (
                      <img
                        src={store.logo_url}
                        alt={`${store.name} logo`}
                        className={styles.storeIconImage}
                      />
                    ) : (
                      <img
                        src={getStoreIcon(store.category)}
                        alt={`${store.name} icon`}
                        className={styles.storeIconImage}
                      />
                    )}
                  </div>

                  <div className={styles.storeInfo}>
                    <Typography
                      variant="heading3"
                      fontFamily="satoshi"
                      weight="bold"
                      className={styles.storeName}>
                      {store.name}
                    </Typography>
                    <Typography
                      variant="bodySmall"
                      fontFamily="satoshi"
                      weight="regular"
                      className={styles.storeItems}>
                      {store.category} • {store.location}
                    </Typography>
                  </div>

                  <div className={styles.storeArrow}>
                    <img
                      src={NewsLetterChevron}
                      alt="Arrow"
                      className={styles.arrowIcon}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};